import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { FeeCategory } from '@/apis/extended/admin/interestRateApi';
import type { ProductEsitmation } from '@/types/ProductType';

export type CalculatedFeeKey =
  | 'appraisalFeeBase'
  | 'creditReportFeeBase'
  | 'processingFeeBase'
  | 'underwritingFee'
  | 'applicationFeeBase'
  | 'floodMonitoringFee'
  | 'floodDeterminationFee'
  | 'rentalIncomeReportFee'
  | 'commitmentFee'
  | 'bridgeFee'
  | 'exitFee'
  | 'originationFee'
  | 'secondMortgageFee'
  | 'additionalLenderOriginationFee'
  | 'environmentalPhaseIFee'
  | 'propertyConditionAssessmentFee'
  | 'commercialCounselFee'
  | 'recordingFees'
  | 'transferTaxes'
  | 'titleSettlementAgentFee'
  | 'titleLendersTitleInsuranceBase'
  | 'optionalOwnersTitleInsurance'
  | 'surveyFee'
  | 'upfrontHomeownersInsurance'
  | 'prepaidsPropertyTaxes'
  | 'prepaidsHomeownersInsurance'
  | 'prepaidInterest'
  | 'upfrontMIPfee'
  | 'upfrontGuaranteeFee'
  | 'vaFundingFee'
  | 'titleSearchFee'
  | 'insuranceBinder'
  | 'ownersTitleInsurance'
  | 'pestInspectionFee'
  | 'initialEscrowTaxes'
  | 'lenderCredits';

export type CalculatedFeeCatalogItem = {
  key: CalculatedFeeKey;
  label: string;
  defaultDescription: string;
  category: FeeCategory;
  // compute amount from estimation
  compute: (est: ProductEsitmation) => number;
  // default visibility/APR inclusion when no admin rule exists
  defaultVisible: boolean;
  defaultIncludedInApr: boolean;
  // if false, omit item entirely for loan types that don't support it
  isApplicable?: (est: ProductEsitmation) => boolean;
  /**
   * Calculator variables (estimation.closingCostParts keys) this line
   * itemizes. Defaults to [key].
   */
  parts?: string[];
  /**
   * Shown in the list but not part of cash to close (financed into the loan
   * or paid outside closing). The text explains why.
   */
  notPaidAtClosing?: string;
};

const safeNum = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const CALCULATED_FEE_CATALOG: CalculatedFeeCatalogItem[] = [
  {
    key: 'appraisalFeeBase',
    label: 'Appraisal Fee',
    defaultDescription:
      'Cost of the property appraisal required by the lender.',
    category: 'lender',
    compute: (e) => safeNum((e as any).appraisalFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'creditReportFeeBase',
    label: 'Credit Report',
    defaultDescription: 'Fee to pull and review borrower credit reports.',
    category: 'lender',
    compute: (e) => safeNum((e as any).creditReportFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'processingFeeBase',
    label: 'Processing Fee',
    defaultDescription: 'Lender processing and file preparation fee.',
    category: 'lender',
    compute: (e) => safeNum((e as any).processingFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'underwritingFee',
    label: 'Underwriting Fee',
    defaultDescription: 'Fee for underwriting review and loan approval.',
    category: 'lender',
    compute: (e) => safeNum((e as any).underwritingFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'applicationFeeBase',
    label: 'Application Fee',
    defaultDescription: 'Fee charged when the loan application is submitted.',
    category: 'lender',
    compute: (e) => safeNum((e as any).applicationFeeBase),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'floodMonitoringFee',
    label: 'Flood Monitoring',
    defaultDescription: 'Monitoring service for flood zone status changes.',
    category: 'lender',
    compute: (e) => safeNum((e as any).floodMonitoringFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'floodDeterminationFee',
    label: 'Flood Determination',
    defaultDescription: 'Determines whether the property is in a flood zone.',
    category: 'lender',
    compute: (e) => safeNum((e as any).floodDeterminationFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'rentalIncomeReportFee',
    label: 'Rental Income Report',
    defaultDescription: 'Report used to verify rental income for investors.',
    category: 'lender',
    compute: (e) => safeNum((e as any).rentalIncomeReportFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
    isApplicable: (e) =>
      (e as any).type === USER_LOAN_PROGRAM.DSCR ||
      (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'originationFee',
    label: 'Origination Fee',
    defaultDescription: 'Lender origination fee on the home equity loan.',
    category: 'lender',
    compute: (e) => safeNum((e as any).originationFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.HELOAN,
  },
  {
    key: 'secondMortgageFee',
    label: 'Second Mortgage Origination Fee',
    defaultDescription: 'Lender origination fee on the second mortgage.',
    category: 'lender',
    compute: (e) => safeNum((e as any).secondMortgageFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.SECOND_MORTGAGE,
  },
  {
    key: 'commitmentFee',
    label: 'Commitment Fee',
    defaultDescription: 'Fee to reserve lender capital for the loan.',
    category: 'lender',
    compute: (e) => safeNum((e as any).commitmentFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'bridgeFee',
    label: 'Bridge Origination Fee',
    defaultDescription: 'Upfront bridge loan origination fee.',
    category: 'lender',
    compute: (e) => safeNum((e as any).bridgeFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.BRIDGE,
  },
  {
    key: 'exitFee',
    label: 'Bridge Exit Fee',
    defaultDescription: 'Fee due at bridge loan payoff or exit.',
    category: 'lender',
    compute: (e) => safeNum((e as any).exitFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.BRIDGE,
  },
  {
    key: 'additionalLenderOriginationFee',
    label: 'Additional Origination Fee',
    defaultDescription:
      'Extra origination when prepayment penalty is removed on DSCR loans.',
    category: 'lender',
    compute: (e) => safeNum((e as any).additionalLenderOriginationFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.DSCR,
  },
  {
    key: 'environmentalPhaseIFee',
    label: 'Environmental Phase I',
    defaultDescription: 'Phase I environmental site assessment.',
    category: 'lender',
    compute: (e) => safeNum((e as any).environmentalPhaseIFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'propertyConditionAssessmentFee',
    label: 'Property Condition Assessment',
    defaultDescription: 'Commercial property condition / engineering report.',
    category: 'lender',
    compute: (e) => safeNum((e as any).propertyConditionAssessmentFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'commercialCounselFee',
    label: 'Loan Counsel Fee',
    defaultDescription: 'Attorney or loan counsel review for commercial files.',
    category: 'lender',
    compute: (e) => safeNum((e as any).commercialCounselFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.COMMERCIAL,
  },
  {
    key: 'recordingFees',
    parts: [
      'countyRecordingFees',
      'stateRecordingFees',
      'recordingTitle',
      'recordingMortgage',
      'recordingDeedBase',
    ],
    label: 'Recording Fees',
    defaultDescription:
      'County and state recording charges for loan documents.',
    category: 'government',
    compute: (e) =>
      safeNum((e as any).countyRecordingFees) +
      safeNum((e as any).stateRecordingFees) +
      safeNum((e as any).recordingTitle) +
      safeNum((e as any).recordingMortgage) +
      safeNum((e as any).recordingDeedBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'transferTaxes',
    parts: ['transferTaxes', 'mortgageTaxes'],
    label: 'Transfer Taxes',
    defaultDescription: 'Government taxes due on property transfer.',
    category: 'government',
    compute: (e) =>
      safeNum((e as any).transferTaxes) + safeNum((e as any).mortgageTaxes),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'titleSettlementAgentFee',
    label: 'Title & Settlement',
    defaultDescription: 'Title company and settlement services fee.',
    category: 'title',
    compute: (e) => safeNum((e as any).titleSettlementAgentFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'titleLendersTitleInsuranceBase',
    label: "Lender's Title Insurance",
    defaultDescription: 'Title insurance policy protecting the lender.',
    category: 'title',
    compute: (e) => safeNum((e as any).titleLendersTitleInsuranceBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'optionalOwnersTitleInsurance',
    label: "(Optional) Owner's Title Insurance",
    defaultDescription: 'Optional title insurance policy for the buyer.',
    category: 'title',
    compute: (e) => safeNum((e as any).optionalOwnersTitleInsurance),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'surveyFee',
    label: 'Survey Fee',
    defaultDescription: 'Fee for property survey or boundary verification.',
    category: 'title',
    compute: (e) => safeNum((e as any).surveyFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'upfrontHomeownersInsurance',
    label: 'Homeowners Policy (first year)',
    defaultDescription: 'Initial premium for homeowners insurance.',
    notPaidAtClosing:
      'Usually paid to your insurance company before closing, so it is not part of cash to close.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).upfrontHomeownersInsurance),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'prepaidsPropertyTaxes',
    label: 'Prepaid Taxes (Escrow)',
    defaultDescription: 'Prepaid property taxes collected for escrow.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).prepaidsPropertyTaxes),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'prepaidsHomeownersInsurance',
    label: 'Prepaid Insurance (Escrow)',
    defaultDescription: 'Prepaid homeowners insurance for escrow.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).prepaidsHomeownersInsurance),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'prepaidInterest',
    label: 'Prepaid Interest',
    defaultDescription: 'Interest due from closing date to first payment.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).prepaidInterest),
    defaultVisible: true,
    defaultIncludedInApr: true,
  },
  {
    key: 'upfrontMIPfee',
    notPaidAtClosing:
      'Financed into the FHA loan amount — not paid in cash at closing.',
    label: 'Upfront MIP Fee',
    defaultDescription: 'Upfront mortgage insurance premium for FHA loans.',
    category: 'government',
    compute: (e) => safeNum((e as any).upfrontMIPfee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.FHA,
  },
  {
    key: 'upfrontGuaranteeFee',
    notPaidAtClosing:
      'Financed into the USDA loan amount — not paid in cash at closing.',
    label: 'Upfront Guarantee Fee',
    defaultDescription: 'Upfront guarantee fee for USDA loans.',
    category: 'government',
    compute: (e) => safeNum((e as any).upfrontGuaranteeFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.USDA,
  },
  {
    key: 'vaFundingFee',
    notPaidAtClosing:
      'Financed into the VA loan amount — not paid in cash at closing.',
    label: 'VA Funding Fee',
    defaultDescription: 'Funding fee charged on VA loans.',
    category: 'government',
    // The VA calculator returns it as upfrontFundingFee.
    compute: (e) =>
      safeNum((e as any).vaFundingFee ?? (e as any).upfrontFundingFee),
    defaultVisible: true,
    defaultIncludedInApr: true,
    isApplicable: (e) => (e as any).type === USER_LOAN_PROGRAM.VA,
  },
  {
    key: 'titleSearchFee',
    parts: ['titleTitleSearchBase'],
    label: 'Title Search',
    defaultDescription:
      'Search of public records for liens, judgments and ownership history.',
    category: 'title',
    compute: (e) => safeNum((e as any).titleTitleSearchBase),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'insuranceBinder',
    label: 'Insurance Binder',
    defaultDescription:
      'Proof of homeowners insurance the lender and title company need before closing.',
    category: 'title',
    compute: (e) => safeNum((e as any).insuranceBinder),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'ownersTitleInsurance',
    parts: ['titleOwnersTitleInsuranceBase'],
    label: "Owner's Title Insurance",
    defaultDescription:
      "Title insurance protecting you as owner (cash purchases have no lender's policy).",
    category: 'title',
    compute: () => 0,
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'pestInspectionFee',
    label: 'Pest Inspection',
    defaultDescription:
      'Wood-destroying insect inspection required by this loan program.',
    category: 'other',
    // Calculators that report closingCostParts itemize it from there;
    // Bridge has no parts but still charges it.
    compute: (e) => safeNum((e as any).pestInspectionFee),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'initialEscrowTaxes',
    label: 'Initial Escrow Deposit (Taxes)',
    defaultDescription:
      'Months of property taxes deposited into your escrow account at closing so the first tax bill can be paid.',
    category: 'prepaid',
    compute: (e) => safeNum((e as any).initialEscrowTaxes),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
  {
    key: 'lenderCredits',
    label: 'Lender Credit',
    defaultDescription:
      'Credit from the lender toward your closing costs (shown as a negative amount).',
    category: 'lender',
    compute: (e) => -safeNum((e as any).lenderCredits),
    defaultVisible: true,
    defaultIncludedInApr: false,
  },
];
